import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { Sparkles, Lock, Crown } from "lucide-react";
import { useT } from "@/lib/i18n";
import { auth } from "@/lib/api";

/**
 * Paywall card shown when a guest hits their quota or a non-Premium user
 * tries a Premium-only feature.
 *
 * Props:
 *   feature: string (machine name for analytics)
 *   reason: 'quota' | 'premium_only' | 'credits'
 */
export default function Paywall({ feature, reason = "quota", cost, credits }) {
  const { lang } = useT();
  const navigate = useNavigate();
  const isLoggedIn = !!auth.getToken();

  const title =
    reason === "premium_only"
      ? lang === "fr" ? "Cette fonctionnalité est réservée à geoVINUM AI" : "Premium-only feature"
      : reason === "credits"
      ? lang === "fr" ? `Crédits insuffisants (${credits}/${cost})` : `Not enough credits (${credits}/${cost})`
      : lang === "fr" ? "Vous avez atteint la limite gratuite" : "You've reached the free limit";

  const subtitle =
    reason === "premium_only"
      ? lang === "fr"
        ? "geoVINUM AI répond avec rigueur sur les sols, climats, géologie, parcelles, cépages — adapté à votre niveau."
        : "geoVINUM AI answers with rigour on soils, climates, geology, plots, grapes — tailored to your level."
      : reason === "credits"
      ? lang === "fr"
        ? "Rechargez vos crédits pour continuer à générer des contenus IA."
        : "Top up credits to keep generating AI content."
      : lang === "fr"
      ? "Passez à geoVINUM AI Amateur ou Pro pour un usage illimité + sauvegarde de vos contenus."
      : "Upgrade to geoVINUM AI Amateur or Pro for unlimited use + content saving.";

  return (
    <div
      className="rounded-3xl border border-[#E5276A]/25 bg-gradient-to-br from-[#FCE4ED] via-white to-[#FBF7F3] p-7 md:p-10 text-center"
      data-testid={`paywall-${feature}`}
    >
      <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-[#E5276A] text-white">
        {reason === "premium_only" ? <Crown className="w-6 h-6" /> : <Lock className="w-6 h-6" />}
      </div>
      <h2 className="font-display text-3xl md:text-4xl text-[#1F3A5F] mt-4 leading-tight">{title}</h2>
      <p className="text-[#4A5568] mt-2 max-w-xl mx-auto">{subtitle}</p>

      <div className="mt-7 grid sm:grid-cols-2 gap-4 max-w-2xl mx-auto text-left">
        <div className="rounded-2xl border-2 border-[#E5276A]/30 bg-white p-5 relative" data-testid="paywall-amateur">
          <span className="absolute -top-2.5 left-5 chip-pink text-[10px] uppercase tracking-wider">
            {lang === "fr" ? "Amateur de vin" : "Wine lover"}
          </span>
          <h3 className="font-display text-2xl text-[#E5276A] mt-1">Amateur</h3>
          <div className="text-3xl font-bold text-[#1F3A5F] mt-1">
            29 €<span className="text-sm text-[#8B95A1] font-normal">/{lang === "fr" ? "mois" : "mo"}</span>
          </div>
          <ul className="mt-4 text-sm text-[#4A5568] space-y-1.5">
            <li className="flex gap-2"><Sparkles className="w-3.5 h-3.5 text-[#E5276A] shrink-0 mt-0.5" /> {lang === "fr" ? "geoVINUM AI illimité" : "Unlimited geoVINUM AI"}</li>
            <li className="flex gap-2"><Sparkles className="w-3.5 h-3.5 text-[#E5276A] shrink-0 mt-0.5" /> {lang === "fr" ? "Terroir Expert (sols, climats…)" : "Terroir Expert (soils, climates…)"}</li>
            <li className="flex gap-2"><Sparkles className="w-3.5 h-3.5 text-[#E5276A] shrink-0 mt-0.5" /> {lang === "fr" ? "Mode éducatif adaptatif" : "Adaptive educational mode"}</li>
            <li className="flex gap-2"><Sparkles className="w-3.5 h-3.5 text-[#E5276A] shrink-0 mt-0.5" /> {lang === "fr" ? "Photo Explain illimité" : "Unlimited Photo Explain"}</li>
            <li className="flex gap-2"><Sparkles className="w-3.5 h-3.5 text-[#E5276A] shrink-0 mt-0.5" /> 100 {lang === "fr" ? "crédits / mois" : "credits / mo"}</li>
          </ul>
          <button
            data-testid="paywall-cta-amateur"
            onClick={() => (isLoggedIn ? navigate("/pricing?plan=amateur") : navigate("/register?next=/pricing"))}
            className="mt-5 w-full px-4 py-2.5 rounded-xl bg-[#E5276A] text-white font-bold hover:bg-[#C01661]"
          >
            {lang === "fr" ? "Choisir Amateur" : "Choose Amateur"}
          </button>
        </div>

        <div className="rounded-2xl border-2 border-[#1F3A5F]/20 bg-white p-5 relative" data-testid="paywall-pro">
          <span className="absolute -top-2.5 left-5 chip-navy text-[10px] uppercase tracking-wider">
            {lang === "fr" ? "Producteurs" : "Producers"}
          </span>
          <h3 className="font-display text-2xl text-[#1F3A5F] mt-1">Pro</h3>
          <div className="text-3xl font-bold text-[#1F3A5F] mt-1">
            49 €<span className="text-sm text-[#8B95A1] font-normal">/{lang === "fr" ? "mois" : "mo"}</span>
          </div>
          <ul className="mt-4 text-sm text-[#4A5568] space-y-1.5">
            <li className="flex gap-2"><Crown className="w-3.5 h-3.5 text-[#1F3A5F] shrink-0 mt-0.5" /> {lang === "fr" ? "Tout Amateur +" : "All Amateur +"}</li>
            <li className="flex gap-2"><Sparkles className="w-3.5 h-3.5 text-[#1F3A5F] shrink-0 mt-0.5" /> AI Onboarding (10 min)</li>
            <li className="flex gap-2"><Sparkles className="w-3.5 h-3.5 text-[#1F3A5F] shrink-0 mt-0.5" /> Wine Tourism & Game Builder</li>
            <li className="flex gap-2"><Sparkles className="w-3.5 h-3.5 text-[#1F3A5F] shrink-0 mt-0.5" /> Story Builder · Conversion Engine</li>
            <li className="flex gap-2"><Sparkles className="w-3.5 h-3.5 text-[#1F3A5F] shrink-0 mt-0.5" /> Map Builder & Designer</li>
            <li className="flex gap-2"><Sparkles className="w-3.5 h-3.5 text-[#1F3A5F] shrink-0 mt-0.5" /> 300 {lang === "fr" ? "crédits / mois" : "credits / mo"}</li>
          </ul>
          <button
            data-testid="paywall-cta-pro"
            onClick={() => (isLoggedIn ? navigate("/pricing?plan=pro") : navigate("/register?next=/pricing"))}
            className="mt-5 w-full px-4 py-2.5 rounded-xl bg-[#1F3A5F] text-white font-bold hover:bg-[#2A4870]"
          >
            {lang === "fr" ? "Choisir Pro" : "Choose Pro"}
          </button>
        </div>
      </div>

      {!isLoggedIn && (
        <p className="mt-6 text-xs text-[#8B95A1]">
          {lang === "fr" ? "Pas de compte ?" : "No account?"}{" "}
          <Link to="/register" className="text-[#E5276A] font-bold underline" data-testid="paywall-register">
            {lang === "fr" ? "Inscription gratuite" : "Free sign up"}
          </Link>
        </p>
      )}
    </div>
  );
}
